"use client";

import type { Monaco } from "@monaco-editor/react";
import type { editor } from "monaco-editor";
import type { MonacoYaml } from "monaco-yaml";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { configureMonacoYaml } from "monaco-yaml";
import { registerCompletion } from "monacopilot";

import type { SupportedLang } from "@acme/validators";
import { Badge } from "@acme/ui/badge";
import { toast } from "@acme/ui/toast";

import { useMonacoEditor } from "../_hooks/use-monaco-editor";
import { useStudioStore } from "../_hooks/use-studio-store";

interface YamlAstGrepEditorProps {
  editorTheme: string;
  monacoEditorOptions: editor.IEditorOptions;
  useGenericSchema?: boolean;
}

const PATH = "file:///input.ast-grep.yaml";

export function YamlAstGrepEditor({
  editorTheme,
  monacoEditorOptions,
  useGenericSchema = false,
}: YamlAstGrepEditorProps) {
  const { monacoEditorModule, monacoReactModule } = useMonacoEditor();
  const lang = useStudioStore((state) => state.lang);

  const setConfig = useStudioStore((state) => state.setConfig);
  const setLang = useStudioStore((state) => state.overrideLang);
  const config = useStudioStore((state) => state.config);

  const containsTs = useMemo(() => {
    return /^language:\s*(ts|typescript)$/im.test(config);
  }, [config]);

  const containsJs = useMemo(() => {
    return /^language:\s*(js|javascript)$/im.test(config);
  }, [config]);

  const containsTsx = useMemo(() => {
    return /^language:\s*tsx$/im.test(config);
  }, [config]);

  const _currentLang = useMemo(() => {
    return /^language:\s*(\w+)$/im.exec(config)?.[1]?.toLowerCase() as
      | (string & {})
      | SupportedLang
      | undefined;
  }, [config]);

  const langOverride = useMemo(() => {
    if (containsTsx && lang !== "tsx") {
      return "tsx";
    }
    if (containsTs && lang !== "typescript") {
      return "typescript";
    }
    if (containsJs && lang !== "javascript") {
      return "javascript";
    }
    return null;
  }, [containsJs, containsTs, containsTsx, lang]);

  const ruleFileNames = useMemo(() => {
    // TODO(riley): Ast-grep does not come with the angular language, we need to make our own schema
    if (lang === "angular" || useGenericSchema) {
      return ["rule"];
    }
    if (lang === "c_sharp") {
      return ["csharp_rule"];
    }

    const newLang = langOverride ?? lang;
    return [`${newLang}_rule`];
  }, [lang, langOverride, useGenericSchema]);

  useEffect(() => {
    if (langOverride && !useGenericSchema) {
      const toastId = toast.warning(`Language mismatch`, {
        description: (
          <div>
            <Badge className="font-mono" variant="outline">
              {lang}
            </Badge>{" "}
            →{" "}
            <Badge className="font-mono" variant="outline">
              {langOverride}
            </Badge>
          </div>
        ),
        dismissible: true,
        action: {
          label: `Switch to ${langOverride}`,
          onClick: () => {
            toast.dismiss(toastId);
            setLang(langOverride as SupportedLang);
          },
        },
      });

      return () => {
        toast.dismiss(toastId);
      };
    }
  }, [lang, langOverride, setLang, useGenericSchema]);

  const schemas = useMemo(() => {
    return ruleFileNames.map((fileName) => ({
      uri: `https://raw.githubusercontent.com/ast-grep/ast-grep/refs/heads/main/schemas/${fileName}.json`,
      fileMatch: ["*.ast-grep.yaml"],
    }));
  }, [ruleFileNames]);

  const updateMonacoRef = useRef<MonacoYaml | null>(null);

  const handleAstGrepYamlEditorMount = useCallback(
    (editor: editor.IStandaloneCodeEditor, monaco: Monaco) => {
      void registerCompletion(monaco, editor, {
        language: "yaml",
        endpoint: "/api/studio/copilot",
      });
      updateMonacoRef.current = configureMonacoYaml(monaco, {
        enableSchemaRequest: true,
        hover: true,
        completion: true,
        validate: true,
        format: true,
        schemas,
      });
    },
    [schemas],
  );

  useEffect(() => {
    if (updateMonacoRef.current) {
      void updateMonacoRef.current.update({
        schemas,
      });
    }
  }, [schemas]);

  const handleChange = useCallback(
    (value: string | undefined) => {
      setConfig(value ?? "");
    },
    [setConfig],
  );

  if (!monacoEditorModule || !monacoReactModule) {
    return null;
  }

  const Editor = monacoReactModule.Editor;

  return (
    <Editor
      className="h-full w-full"
      defaultLanguage="yaml"
      value={config}
      onChange={handleChange}
      theme={editorTheme}
      options={monacoEditorOptions}
      onMount={handleAstGrepYamlEditorMount}
      path={PATH}
    />
  );
}
